import { round } from 'lodash-es'
import { DEFAULT_VIEWPORT_WIDTH, MAX_DISPLAY_WIDTH } from '~/config/global'

export function getIP(headers?: HeadersInit) {
  const FIELDS = ['x-original-forwarded-for', 'proxy-client-ip', 'x-client-ip', 'x-true-ip', 'x-forwarded-for']
  for (const field of FIELDS) {
    if (!headers) {
      return
    }
    const value = Array.isArray(headers) ? headers.find(header => header[0] === field)?.[1] : (headers as Record<string, string>)[field]
    if (value !== null && value !== undefined && value.length > 0) {
      return value
    }
  }
  return null
}

// 防抖函数
export function debounce(func: any, wait: any) {
  let timeout: NodeJS.Timeout | null = null

  return function (...args: any[]) {
    const later = () => {
      timeout = null
      func(...args)
    }

    if (timeout !== null) {
      clearTimeout(timeout)
    }

    timeout = setTimeout(later, wait)
  }
}
// 返回简写
export function getShortName(name: string) {
  let abbName = name
  switch (name) {
    case '略有磨损':
      abbName = '略磨'
      break
    case '破损不堪':
      abbName = '破损'
      break
    case '崭新出厂':
      abbName = '崭新'
      break
    case '久经沙场':
      abbName = '久经'
      break
    case '战痕累累':
      abbName = '战痕'
      break
    case '纪念品':
      abbName = '纪'
      break
    case 'StatTrak™':
      abbName = 'ST'
      break
    case '★':
      abbName = '★'
      break
    case '英雄传世':
      abbName = '英雄'
      break
    case '亲笔签名':
      abbName = '亲笔'
      break
    default:
      break
  }
  return abbName
}

// 获取地址栏通用参数
export const getQueryParams = () => {
  const search = window.location.search
  const urlParams = new URLSearchParams(search)
  const queryParams = {} as any

  for (const [key, value] of urlParams.entries()) {
    queryParams[key] = value
  }

  return queryParams
}

// px转vw
export const pxTransformVw = (style: any) => {
  if (!style) {
    return '0vw'
  }
  const num = style?.toString().replace('px', '')
  if (process.server) {
    return `${(num / 3.75).toFixed(2)}vw`
  }

  const width = window.screen.width
  const ratio = width / 100

  // console.log(`width: ${width}; ratio: ${ratio}`)

  return `${(num / ratio).toFixed(2)}vw`
}

export const pxToClampVw = (px: number) => {
  if (px === 0) return 0

  const vw = round((px * 100) / DEFAULT_VIEWPORT_WIDTH, 3)
  const maxPx = round((px * MAX_DISPLAY_WIDTH) / DEFAULT_VIEWPORT_WIDTH, 3)
  const cssF = px > 0 ? 'min' : 'max'
  return `${cssF}(${vw}vw, ${maxPx}px)`
}

export const disableZoom = () => {
  // e.preventDefault()  ===    阻止默认事件

  // 当一个手指放在屏幕上时，会触发 touchstart 事件。如果另一个手指又放在了屏幕上，则会先触发 gesturestart 事件
  document.addEventListener('gesturestart', function (e) {
    e.preventDefault()
  })

  // 在单个元素上单击两次 ===  dblclick
  document.addEventListener('dblclick', function (e) {
    e.preventDefault()
  })

  // 一个手指放在屏幕上时，会触发 touchstart 事件
  document.addEventListener('touchstart', function (event) {
    if (event.touches.length > 1) {
      event.preventDefault()
    }
  })

  // 如果一个或两个手指在屏幕上滑动，将会触发 gesturechange 事件
  // 但只要有一个手指移开， 就会触发 gestureend 事件，紧接着又会触发基于该手指的 touchend 事件
  let lastTouchEnd = 0
  document.addEventListener(
    'touchend',
    function (event) {
      const now = new Date().getTime()
      // 如果在300ms内触发两次touchend，就阻止默认事件
      if (now - lastTouchEnd <= 100) {
        event.preventDefault()
      }
      lastTouchEnd = now
    },
    false
  )
}

// 是否是全面屏
export const isAllScreenDisplay = () => {
  if (typeof window !== 'undefined' && window) {
    return (
      /iphone/gi.test(window.navigator.userAgent) && window.screen.height >= 724 // 原为812
    )
  }
  return false
}

export const getVantNavBarHeight = () => {
  if (typeof window !== 'undefined' && window) {
    const element = document.getElementsByClassName('van-nav-bar')?.[0]

    if (element) {
      // 获取元素的计算样式
      const computedStyle = window.getComputedStyle(element)

      // 计算内容高度（不包括padding和margin）
      return Number((element.clientHeight - parseFloat(computedStyle.paddingTop) - parseFloat(computedStyle.paddingBottom)).toFixed(2))
    }
  }
  return 0
}

export const getVantTabBarHeight = () => {
  if (typeof window !== 'undefined' && window) {
    const element = document.getElementsByClassName('van-tabbar')?.[0]

    if (element) {
      // 获取元素的计算样式
      const computedStyle = window.getComputedStyle(element)

      // 计算内容高度（不包括padding和margin）
      return Number((element.clientHeight - parseFloat(computedStyle.paddingTop) - parseFloat(computedStyle.paddingBottom)).toFixed(2))
    }
  }
  return 0
}

// 处理seo域名
export const getSeoUrl = (url: string) => {
  const hostname = window.location.hostname
  const startIndex = hostname.indexOf('.') + 1
  const domainLatter = hostname.substring(startIndex, hostname.length)
  const protocol = window.location.protocol
  return `${protocol}//c5.${domainLatter}${url}`
}

// url转base64
export const urlToBase64 = (url: string) => {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const reader = new FileReader()

        reader.onloadend = () => {
          resolve(reader.result)
        }

        reader.readAsDataURL(blob)
      })
      .catch(() => {
        reject(new Error('Failed to load the url'))
      })
  })
}

// 深拷贝
export const deepClone = (obj: object) => {
  const _obj = JSON.stringify(obj)
  const objClone = JSON.parse(_obj)

  return objClone
}

export async function copyToClipboard(text?: string, msg?: string) {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    showToast(msg || '复制成功')
  } catch (err) {
    console.error('复制失败: ', err)
  }
}

export const isToday = (timestamp: any) => {
  const today = new Date()
  const todayYear = today.getFullYear()
  const todayMonth = today.getMonth() + 1
  const todayDay = today.getDate()

  const curDate = new Date(Number(timestamp))
  const curDateYear = curDate.getFullYear()
  const curDateMonth = curDate.getMonth() + 1
  const curDateDay = curDate.getDate()

  if (todayYear === curDateYear && todayMonth === curDateMonth && todayDay === curDateDay) {
    return true
  } else {
    return false
  }
}

export const parseJWT = (token: string) => {
  try {
    if (!token) return null
    const parts = token.split('.')
    if (parts.length !== 3) return null

    // Base64解码payload部分
    const payload = parts[1]
    // 处理Base64 URL安全编码
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = atob(base64)
    return JSON.parse(decoded)
  } catch (error) {
    console.error('JWT解析失败:', error)
    return null
  }
}

const getFingerprint = async () => {
  const fp = await FingerprintJS.load()
  const result = await fp.get()
  const visitorId = result.visitorId

  return visitorId
}

export const getDeviceId = async () => {
  let deviceId = Cookie.get('C5DeviceId')
  if (deviceId) {
    return deviceId
  }

  deviceId = await getFingerprint()

  Cookie.set('C5DeviceId', deviceId)

  return deviceId
}

const generateSign = ({
  pathname,
  accessToken,
  timestamp,
  method
}: {
  pathname: string
  accessToken: string
  timestamp: number
  method: string
}) => {
  // 获取pathname 并规范化路径，类似Java的URIBuilder.normalize()
  // 确保前面有斜杠，后面也有斜杠，处理重复斜杠
  let normalizedPath = pathname
  // 去除开头多余的斜杠，只保留一个
  normalizedPath = normalizedPath.replace(/^\/+/, '/')
  // 去除结尾多余的斜杠，只保留一个
  normalizedPath = normalizedPath.replace(/\/+$/, '/')
  // 确保以斜杠开头
  if (!normalizedPath.startsWith('/')) {
    normalizedPath = '/' + normalizedPath
  }
  // 确保以斜杠结尾
  if (!normalizedPath.endsWith('/')) {
    normalizedPath = normalizedPath + '/'
  }

  // 获取method 大写
  const A = method.toLocaleUpperCase()

  // 规范化后的请求路径
  const B = normalizedPath

  // MagicCode 魔数
  const C = normalizedPath.length % 6

  // 请求时间戳
  const D = timestamp

  // 解析JWT获取用户信息
  const jwtPayload = parseJWT(accessToken)

  // 密钥 - 可以使用JWT中的特定字段，如用户ID等
  const E = jwtPayload?.sct

  const sign = CryptoJS.MD5(A + '|' + B + '|' + C + '|' + D + '|' + E).toString()
  console.log('sign', A + '|' + B + '|' + C + '|' + D + '|' + E, sign)
  return sign
}

export const generateCustomHeader = async (config: any) => {
  const customHeader = {} as any
  const timestamp = +new Date()

  if (Cookie.get('C5AccessToken')) {
    customHeader['x-access-token'] = Cookie.get('C5AccessToken')
  } else if (Cookie.get('H5_C5_AccessToken')) {
    customHeader['x-access-token'] = Cookie.get('H5_C5_AccessToken')
  }

  // 生物识别
  customHeader['x-device-id'] = await getDeviceId()
  customHeader['x-start-req-time'] = timestamp
  customHeader['x-source'] = isInApp ? (Cookie.get('C5Channel') === 'iOS' ? 3 : 2) : 4
  customHeader['x-app-channel'] = Cookie.get('C5Channel') || 'iOS'
  customHeader['accept-language'] = config.headers.language ? config.headers.language : 'zh-CN'
  customHeader['user-agent'] = 'C5GAME App'
  customHeader['x-app-version-code'] = Cookie.get('C5AppVersion') || '401000'
  customHeader['x-ua'] = config.headers.userAgent
  console.log('config.url', config.url)
  customHeader['x-sign'] = generateSign({
    pathname: config.url,
    accessToken: customHeader['x-access-token'],
    timestamp,
    method: config.method || 'GET'
  })

  const douyuTag = Cookie.get('douyuTag') ? Cookie.get('douyuTag') : ''
  const from = Cookie.get('NC5_sem_from') ? Cookie.get('NC5_sem_from') : ''
  const keyword = Cookie.get('NC5_sem_keyword') ? Cookie.get('NC5_sem_keyword') : ''
  const clickExt = Cookie.get('NC5_sem_clickExt') ? Cookie.get('NC5_sem_clickExt') : ''
  const bdvid = Cookie.get('NC5_sem_bdvid') ? Cookie.get('NC5_sem_bdvid') : ''
  const sem360id = Cookie.get('NC5_sem360id') ? Cookie.get('NC5_sem360id') : ''
  const refererUrl = Cookie.get('NC5_sem_refererurl') ? Cookie.get('NC5_sem_refererurl') : ''
  const source = Cookie.get('NC5_activity_from') ? Cookie.get('NC5_activity_from') : ''

  if (douyuTag || from || keyword || bdvid || sem360id || source) {
    customHeader['x-sem-channel'] = `${douyuTag || from || source};${keyword};${bdvid || clickExt || sem360id}`
    customHeader['x-referral-url'] = `${refererUrl}`
  }

  return customHeader
}

/**
 * 格式化数字为千分位分隔的字符串
 * @param {number|string} number - 需要格式化的数字或数字字符串
 * @param {Object} options - 配置选项
 * @param {string} [options.locale='en-US'] - 语言环境
 * @param {string} [options.style='decimal'] - 格式化样式 (decimal, currency, percent)
 * @param {string} [options.currency='USD'] - 货币类型 (仅当 style 为 currency 时有效)
 * @param {number} [options.minimumFractionDigits=2] - 最小小数位数
 * @param {number} [options.maximumFractionDigits=2] - 最大小数位数
 * @returns {string} 格式化后的字符串
 */
export const formatNumber = (number, options = {}) => {
  // 参数默认值
  const { locale = 'en-US', style = 'decimal', currency = 'USD', minimumFractionDigits = 0, maximumFractionDigits = 2 } = options

  // 转换可能的字符串类型的数字为数值类型
  const numericValue = typeof number === 'string' ? parseFloat(number) : number

  // 处理非数字值
  if (isNaN(numericValue)) {
    return number.toString()
  }

  // 配置对象
  const formatOptions = {
    style,
    minimumFractionDigits,
    maximumFractionDigits
  }

  // 如果是货币格式，添加货币类型
  if (style === 'currency') {
    formatOptions.currency = currency
  }

  // 使用 Intl.NumberFormat 进行格式化
  return numericValue.toLocaleString(locale, formatOptions)

  // 使用示例
  // console.log(formatNumber(1234567.89)); // 默认输出: 1,234,567.89
  // console.log(formatNumber(1234567.89, { style: 'currency' })); // 货币格式: $1,234,567.89
  // console.log(formatNumber(1234567.89, { locale: 'zh-CN', style: 'currency', currency: 'CNY' })); // 中文货币格式: ¥1,234,567.89
  // console.log(formatNumber(0.1234, { minimumFractionDigits: 4, maximumFractionDigits: 4 })); // 自定义小数位数: 0.1234
  // console.log(formatNumber(0.5, { style: 'percent' })); // 百分比格式: 50.00%
}
